import { useFormState } from "common/hooks";
import { FrequencyUnit, ItemSelectionKind, UserSubscriptionAction } from "common/types";
import { parseNumber } from "common/utils/numbers";
import { useBuildNext } from "common/router/hooks";

import { OnboardingDetailSubscriptionStepParams } from "client/onboarding/types";
import Button from "client/common/components/buttons/Button";
import {
  useOnboardingSubscriptionQuery,
  useUpdateSubscriptionMutation,
} from "client/subscriptions/hooks";
import { OnboardingDetailSubscription, SubscriptionUpdate } from "client/subscriptions/types";
import QuantityInput from "client/common/components/inputs/QuantityInput";
import SubscriptionFrequencySelect from "client/subscriptions/components/SubscriptionFrequencySelect";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import Error404Page from "client/common/components/errors/Error404Page";
import { useInvalidateOnboarding } from "client/onboarding/hooks/invalidate";
import Select from "client/common/components/inputs/Select";

import StepLayout from "../OnboardingSubscriptionsStepLayout";
import OnboardingSubscriptionItem from "../OnboardingSubscriptionItem";

type SubscriptionMutate = {
  interval_unit: FrequencyUnit;
  frequency: number;
  display: string;
  quantity: number;
  flavor_selection?: number;
};

type OnboardingSubscriptionQuantityFrequencyStepFormProps = {
  subscription: OnboardingDetailSubscription;
  isUpdating: boolean;
  onUpdateSubscription: (values: SubscriptionUpdate) => void;
};

const OnboardingSubscriptionQuantityFrequencyStepForm = ({
  subscription,
  isUpdating,
  onUpdateSubscription,
}: OnboardingSubscriptionQuantityFrequencyStepFormProps) => {
  const subscriptionFrequency = subscription.is_active_frequency_valid
    ? subscription.active_frequency
    : subscription.recommended_frequency;
  const [values, updateField, setValues] = useFormState<SubscriptionMutate>({
    flavor_selection: subscription.flavor_selection || undefined,
    quantity: subscription.quantity,
    interval_unit: subscriptionFrequency.interval_unit,
    display: subscriptionFrequency.display,
    frequency: subscriptionFrequency.frequency,
  });

  return (
    <StepLayout.Form
      canSubmit={!isUpdating}
      onSubmit={() =>
        onUpdateSubscription({
          ...values,
          action: UserSubscriptionAction.Modify,
        })
      }
    >
      <OnboardingSubscriptionItem
        subscription={subscription}
        includeQuantity={false}
        isBoxed={false}
      />
      <QuantityInput min={1} value={values.quantity} onChange={updateField("quantity")} />
      <SubscriptionFrequencySelect
        subscription={subscription}
        value={{
          interval_unit: values.interval_unit,
          frequency: values.frequency,
          display: values.display,
        }}
        onSelect={(newFrequency) => setValues({ ...values, ...newFrequency })}
        activeDisplay="your previous frequency"
      />
      {subscription.item.flavor_options.map((optionsGroup) => (
        <Select
          label={`Select ${optionsGroup.label.toLocaleLowerCase()}`}
          key={ItemSelectionKind.Flavor}
          onChange={(newFlavor) =>
            setValues({
              ...values,
              flavor_selection: parseNumber(newFlavor, undefined),
            })
          }
          value={`${values.flavor_selection || ""}`}
        >
          {optionsGroup.options.map((option) => (
            <Select.Option key={option.id} value={`${option.id}`}>
              {option.display}
            </Select.Option>
          ))}
        </Select>
      ))}
      <StepLayout.Footer>
        <Button type="submit" isLoading={isUpdating}>
          Continue
        </Button>
      </StepLayout.Footer>
    </StepLayout.Form>
  );
};

const OnboardingSubscriptionQuantityFrequencyStep = ({
  id,
  onNext,
  isLoadingNext,
  stepper,
  rootDest,
}: OnboardingDetailSubscriptionStepParams) => {
  const buildNext = useBuildNext();
  const invalidateOnboarding = useInvalidateOnboarding();
  const {
    mutate: updateSubscription,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateSubscriptionMutation(id, {
    onSuccess: () => {
      invalidateOnboarding();
      onNext();
    },
  });
  const { data: subscription, isLoading: isSubscriptionLoading } =
    useOnboardingSubscriptionQuery(id);

  if (isSubscriptionLoading) {
    return <LoadingContainer />;
  }

  if (!subscription) {
    return (
      <Error404Page
        title="Autoship Not Found!"
        description="We could not find that Autoship"
        buttonText="Update other Autoships"
        buttonLink={buildNext(rootDest)}
      />
    );
  }
  return (
    <StepLayout stepper={stepper}>
      {!!updateError && (
        <FetchErrorAlert
          error={updateError}
          fieldErrors={[updateError.errors.quantity, updateError.errors.frequency]}
        />
      )}
      <StepLayout.Title>
        Remind us... How often would you like this product shipped?
      </StepLayout.Title>
      <StepLayout.Description>
        Frequency should be based on the dosage. You and your vet may make adjustments later if
        needed.
        {!!subscription.flavor_selection &&
          " You can also confirm additional selections for your subscription."}
      </StepLayout.Description>
      <OnboardingSubscriptionQuantityFrequencyStepForm
        subscription={subscription}
        isUpdating={isUpdating || isLoadingNext}
        onUpdateSubscription={updateSubscription}
      />
    </StepLayout>
  );
};

export default OnboardingSubscriptionQuantityFrequencyStep;
